import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useUserProfile } from '@/features/profile/hooks/useUserProfile'
import { useAuth } from '@/features/auth/hooks/useAuth'
import {
  bootIntercom,
  showLauncher,
  hideLauncher,
  openIntercomChat,
  shutdownIntercom,
} from '@/lib/intercom'

/**
 * Routes where the Intercom launcher should be HIDDEN.
 */
const HIDDEN_ROUTES = ['/ai', '/sitemap']

function isMobile() {
  return typeof window !== 'undefined' && window.innerWidth < 768
}

function shouldShowLauncher(pathname) {
  if (isMobile()) return false
  return !HIDDEN_ROUTES.some(route => pathname.startsWith(route))
}

/**
 * Hook that manages the full Intercom lifecycle:
 * 1. Boots Intercom with user identity once profile data is available
 * 2. Shows/hides the launcher based on the current route
 *
 * Returns `{ openChat }` for imperative usage (e.g. dropdown button).
 */
export function useIntercom() {
  const { profile } = useUserProfile()
  const { user } = useAuth()
  const location = useLocation()
  const hasBooted = useRef(false)

  // Boot once when user data is ready
  useEffect(() => {
    if (hasBooted.current) return
    const email = profile?.email || user?.email
    if (!email) return

    bootIntercom({
      email,
      name: profile?.name || user?.name || undefined,
      user_id: user?.id || user?._id || undefined,
      ...(profile?.avatar ? { avatar: { type: 'avatar', image_url: profile.avatar } } : {}),
    })
    hasBooted.current = true

    // Immediately apply launcher visibility for the current route
    // (the route-change effect may have already fired before boot completed)
    if (shouldShowLauncher(location.pathname)) {
      showLauncher()
    } else {
      hideLauncher()
    }
  }, [profile, user, location.pathname])

  // Toggle launcher visibility on route changes
  useEffect(() => {
    if (!hasBooted.current) return

    if (shouldShowLauncher(location.pathname)) {
      showLauncher()
    } else {
      hideLauncher()
    }
  }, [location.pathname])

  return { openChat: openIntercomChat }
}
